/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Iterable integration with AI Decisioning", description = "Learn how to connect Iterable to AI Decisioning in Hightouch to send personalized, real-time messages across email, SMS, push, and in-app channels.", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Iterable integration with AI Decisioning",
        description: "Learn how to connect Iterable to AI Decisioning in Hightouch to send personalized, real-time messages across email, SMS, push, and in-app channels.",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "1. Set up Iterable as a destination",
          "depth": 0,
          "id": "1-set-up-iterable-as-a-destination"
        }, {
          "value": "2. Add channels in AI Decisioning",
          "depth": 0,
          "id": "2-add-channels-in-ai-decisioning"
        }, {
          "value": "Set up a channel",
          "depth": 1,
          "id": "set-up-a-channel"
        }, {
          "value": "Identifier type comparison",
          "depth": 1,
          "id": "identifier-type-comparison"
        }, {
          "value": "3. Add messages",
          "depth": 0,
          "id": "3-add-messages"
        }, {
          "value": "Add a message from Iterable",
          "depth": 1,
          "id": "add-a-message-from-iterable"
        }, {
          "value": "Test a message",
          "depth": 1,
          "id": "test-a-message"
        }, {
          "value": "How Iterable templates and campaigns work with Hightouch",
          "depth": 0,
          "id": "how-iterable-templates-and-campaigns-work-with-hightouch"
        }, {
          "value": "What happens behind the scenes",
          "depth": 1,
          "id": "what-happens-behind-the-scenes"
        }, {
          "value": "Why this matters",
          "depth": 1,
          "id": "why-this-matters"
        }, {
          "value": "How to find the right campaign",
          "depth": 1,
          "id": "how-to-find-the-right-campaign"
        }, {
          "value": "4. Configure Iterable Triggered Campaigns for AID",
          "depth": 0,
          "id": "4-configure-iterable-triggered-campaigns-for-aid"
        }, {
          "value": "Create from template",
          "depth": 1,
          "id": "create-from-template"
        }, {
          "value": "Existing campaign",
          "depth": 1,
          "id": "existing-campaign"
        }, {
          "value": "5. Use variables in Iterable templates",
          "depth": 0,
          "id": "5-use-variables-in-iterable-templates"
        }, {
          "value": "Access variables",
          "depth": 1,
          "id": "access-variables"
        }, {
          "value": "Important notes",
          "depth": 1,
          "id": "important-notes"
        }, {
          "value": "Troubleshooting",
          "depth": 0,
          "id": "troubleshooting"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      ol: "ol",
      img: "img",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Technical marketers, marketing and lifecycle teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/prepare-data",
                children: "Prepare data for AID"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Connect Iterable to AI Decisioning so agents can deliver personalized emails, SMS, push notifications, and in-app messages through your existing Iterable Triggered Campaigns."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Delivering relevant and timely customer experiences in Iterable requires up-to-date, contextual data. By integrating Hightouch's AI Decisioning (AID) with Iterable, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Automatically trigger Iterable campaigns based on predictions and behaviors surfaced by AID Agents"
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Personalize message content using dynamic variables from your data warehouse"
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Centralize targeting and decision logic in Hightouch, while using Iterable to handle message delivery"
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "1-set-up-iterable-as-a-destination",
        children: "1. Set up Iterable as a destination"
      }), "\n", _jsxs(_components.p, {
        children: ["Follow the ", _jsx(_components.a, {
          href: "/destinations/iterable",
          children: "destination setup guide"
        }), " up to ", _jsx(_components.strong, {
          children: "Sync Configuration"
        }), ". Once connected, Iterable will be available as a destination in AI Decisioning."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "2-add-channels-in-ai-decisioning",
        children: "2. Add channels in AI Decisioning"
      }), "\n", _jsxs(_components.p, {
        children: ["Once Iterable is set up as a destination, you can add ", _jsx(_components.strong, {
          children: "channels"
        }), ". Channels define how Hightouch sends messages to Iterable via Triggered Campaigns."]
      }), "\n", _jsx(_components.h3, {
        id: "set-up-a-channel",
        children: "Set up a channel"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "AI Decisioning > Configuration > Channels"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add channel"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/iterable/aid-add-channel.png",
          alt: "Add channel button"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Choose a channel type:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Email"
              }), " – for triggered email sends"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "SMS"
              }), " – for triggered text messages"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Push"
              }), " – for mobile push notifications"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "In-App"
              }), " – for in-app messaging (popups, inbox cards, etc.)"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Iterable"
          }), " as the destination."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Campaign setup"
          }), ", choose how Hightouch connects to Iterable:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Create from template"
              }), " – select a template, and Hightouch creates an API-triggered campaign from it."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Existing campaign"
              }), " – select an existing API-triggered campaign directly."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Identifier type"
          }), ", choose how to match users:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Email"
              }), " – match by email address"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "User ID"
              }), " – match by unique ID"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose the identifier field from your model (for example, ", _jsx(_components.code, {
            children: "user.email"
          }), " or ", _jsx(_components.code, {
            children: "user.id"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add channel"
          }), ", then ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/iterable/aid-add-channel-iterable.png",
          alt: "New channel modal"
        })
      }), "\n", _jsx(_components.h3, {
        id: "identifier-type-comparison",
        children: "Identifier type comparison"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Identifier"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Use when"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              children: "Matches users based on email address"
            }), _jsx(_components.td, {
              children: "Campaigns primarily use email for targeting"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "User ID"
            }), _jsx(_components.td, {
              children: "Matches users using their unique ID"
            }), _jsx(_components.td, {
              children: "Source data uses internal IDs or email isn't guaranteed"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Both identifier types support all channel types (Email, SMS, Push, and In-App)."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "3-add-messages",
        children: "3. Add messages"
      }), "\n", _jsxs(_components.p, {
        children: ["After you add channels in AI Decisioning, you can add ", _jsx(_components.strong, {
          children: "messages"
        }), " to your agents. Each message connects an agent's decision to an Iterable Triggered Campaign."]
      }), "\n", _jsxs(_components.p, {
        children: ["For full details on message configuration, variants, and tags, see ", _jsx(_components.a, {
          href: "/ai-decisioning/messages",
          children: "Messages"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "add-a-message-from-iterable",
        children: "Add a message from Iterable"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "AI Decisioning > Agents"
          }), " and select your agent."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Messages"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add messages"
          }), ", then select ", _jsx(_components.strong, {
            children: "Add messages individually"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select your configured Iterable ", _jsx(_components.strong, {
            children: "channel and destination"
          }), " tile (for example, ", _jsx(_components.strong, {
            children: "Email - Iterable"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select an Iterable template (or an existing campaign, matching your channel's ", _jsx(_components.strong, {
            children: "Campaign setup"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "message name"
          }), " (for example, ", _jsx(_components.code, {
            children: "Cart Abandonment SMS"
          }), ") and select ", _jsx(_components.strong, {
            children: "Add messages"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/iterable/aid-iterable-add-message.png",
          alt: "Add message"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["When your channel uses ", _jsx(_components.strong, {
            children: "Create from template"
          }), ", Hightouch ", _jsx(_components.strong, {
            children: "clones"
          }), " the\nselected Iterable template and creates a new Triggered Campaign, then sends\nthrough that new campaign via API — not the original template. See ", _jsx(_components.a, {
            href: "#how-iterable-templates-and-campaigns-work-with-hightouch",
            children: "How\nIterable templates and campaigns work with\nHightouch"
          }), " for\ndetails."]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can repeat this process for each channel type:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Channel type"
            }), _jsx(_components.th, {
              children: "Campaign type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              children: "Triggered"
            }), _jsx(_components.td, {
              children: "Real-time personalized emails"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "SMS"
            }), _jsx(_components.td, {
              children: "Triggered"
            }), _jsx(_components.td, {
              children: "Time-sensitive text alerts"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Push"
            }), _jsx(_components.td, {
              children: "Triggered"
            }), _jsx(_components.td, {
              children: "Native app push notifications"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "In-App"
            }), _jsx(_components.td, {
              children: "Triggered"
            }), _jsx(_components.td, {
              children: "Contextual popups and messages"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "test-a-message",
        children: "Test a message"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open the message and go to the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Optimized content"
          }), ", confirm your variants are configured. If your message uses ", _jsx(_components.a, {
            href: "#use-variables-in-iterable",
            children: "dynamic variables"
          }), ", set values for testing."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Preview"
          }), " panel, verify the rendered message looks correct."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Send test"
          }), " to send the message to a test user in Iterable."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/iterable/aid-iterable-send-test.png",
          alt: "Send test"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-iterable-templates-and-campaigns-work-with-hightouch",
        children: "How Iterable templates and campaigns work with Hightouch"
      }), "\n", _jsxs(_components.p, {
        children: ["When your channel uses ", _jsx(_components.strong, {
          children: "Create from template"
        }), ", you're not sending the base template directly. Instead, Hightouch ", _jsx(_components.strong, {
          children: "clones"
        }), " the template to create a new Triggered Campaign. If your channel uses ", _jsx(_components.strong, {
          children: "Existing campaign"
        }), ", Hightouch sends through the campaign you selected and doesn't clone anything."]
      }), "\n", _jsx(_components.h3, {
        id: "what-happens-behind-the-scenes",
        children: "What happens behind the scenes"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "You select an existing Iterable template (by Template ID)."
        }), "\n", _jsxs(_components.li, {
          children: ["When you select ", _jsx(_components.strong, {
            children: "Add messages"
          }), ", Hightouch:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Clones the template"
            }), "\n", _jsxs(_components.li, {
              children: ["Creates a new ", _jsx(_components.strong, {
                children: "Campaign ID"
              })]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/iterable/aid-campaign-id-ht.png",
          alt: "New campaign"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch uses the ", _jsx(_components.strong, {
            children: "new campaign ID"
          }), " to send messages via API—not the original template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/iterable/aid-iterable-campaign-template-id.png",
          alt: "Campaign ID and Template ID in Iterable UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "why-this-matters",
        children: "Why this matters"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch sends through the ", _jsx(_components.strong, {
            children: "new campaign"
          }), ", not the original."]
        }), "\n", _jsx(_components.li, {
          children: "To update message content, edit the new campaign version—not the base template."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "how-to-find-the-right-campaign",
        children: "How to find the right campaign"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Open the ", _jsx(_components.strong, {
              children: "Content"
            }), " tab in the message:\n", _jsx(_components.strong, {
              children: "AI Decisioning > Agents > [Select Agent] > Messages > [Select Message] > Content"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select the link next to ", _jsx(_components.strong, {
              children: "Base content"
            }), " to open the Iterable campaign."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/iterable/aid-iterable-base-content-link.png",
          alt: "Base content link"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "4-configure-iterable-triggered-campaigns-for-aid",
        children: "4. Configure Iterable Triggered Campaigns for AID"
      }), "\n", _jsxs(_components.p, {
        children: ["What you set up in Iterable depends on your channel's ", _jsx(_components.strong, {
          children: "Campaign setup"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "create-from-template",
        children: "Create from template"
      }), "\n", _jsx(_components.p, {
        children: "Build and save the Iterable template you want to use. Hightouch creates the API-triggered campaign for you when you add the message — you don't need to create a campaign in Iterable."
      }), "\n", _jsx(_components.h3, {
        id: "existing-campaign",
        children: "Existing campaign"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Iterable, go to ", _jsx(_components.strong, {
            children: "Messaging > Campaigns"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "New Campaign > Triggered Campaign"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the message type, design your template, and publish the campaign."
        }), "\n", _jsxs(_components.li, {
          children: ["In Hightouch, select that campaign when ", _jsx(_components.a, {
            href: "#3-add-messages",
            children: "adding a message"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After you add the message, ", _jsx(_components.a, {
          href: "#test-a-message",
          children: "test it"
        }), " to confirm delivery."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx("a", {
        id: "use-variables-in-iterable"
      }), "\n", _jsx(_components.h2, {
        id: "5-use-variables-in-iterable-templates",
        children: "5. Use variables in Iterable templates"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch variables allow you to personalize Iterable messages using live warehouse data, any variable you've defined within the message such as subject line variations, or different content blocks in your Iterable library."
      }), "\n", _jsx(_components.h3, {
        id: "access-variables",
        children: "Access variables"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open a message in Hightouch and go to the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Optimized content"
          }), ", expand a content type (for example, ", _jsx(_components.strong, {
            children: "subject"
          }), " or ", _jsx(_components.strong, {
            children: "body"
          }), ") and add your variants."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.code, {
            children: "..."
          }), " menu on a content type and select ", _jsx(_components.strong, {
            children: "Copy code snippet"
          }), " to get the variable code."]
        }), "\n", _jsx(_components.li, {
          children: "In Iterable, paste the variable tag into the corresponding field in your message template."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/iterable/aid-iterable-copy-code.png",
          alt: "copy code snippet UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "important-notes",
        children: "Important notes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Variables are fully supported in Triggered Campaigns."
        }), "\n", _jsx(_components.li, {
          children: "Make sure your Iterable template references the correct variable syntax from the code snippet."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Issue"
            }), _jsx(_components.th, {
              children: "Likely cause"
            }), _jsx(_components.th, {
              children: "Resolution"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Message fails to send"
            }), _jsx(_components.td, {
              children: "API errors or misconfigured destination"
            }), _jsx(_components.td, {
              children: "Check Hightouch logs for API errors."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Test user not receiving messages"
            }), _jsx(_components.td, {
              children: "Identifier mismatch"
            }), _jsx(_components.td, {
              children: "Verify the identifier (email or user ID) matches a record in Iterable."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Campaign sends not triggering"
            }), _jsx(_components.td, {
              children: "Campaign not published or misconfigured"
            }), _jsx(_components.td, {
              children: "Confirm the campaign is live and triggerable in Iterable."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Variables not rendering"
            }), _jsx(_components.td, {
              children: "Incorrect syntax or missing field mapping"
            }), _jsxs(_components.td, {
              children: ["Double-check variable syntax from ", _jsx(_components.strong, {
                children: "Copy code snippet"
              }), " and field mapping."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/messages",
            children: "Messages"
          }), " to create and manage the message variants each agent sends through Iterable."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/qa-guide",
            children: "QA guide"
          }), " to validate messages before you launch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/agents",
            children: "Agents"
          }), " to configure the agents that decide which messages to send."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/insights",
            children: "Insights"
          }), " to measure how your agents and messages perform."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
